import React, { useState, useEffect, useCallback, useRef } from "react";
import { Input } from "./input"; // Your input component
import { ChevronUpDownIcon } from "@heroicons/react/20/solid";  
import Spinner from './Spinner';

import clsx from "clsx";

export function CategoryTree({ apiUrl, onSelect, value, fullPath, onChange, disabled }) {
  const [categories, setCategories] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [autoExpanded, setAutoExpanded] = useState({});
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(value || null);
  const [selectedPath, setSelectedPath] = useState(fullPath || "");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [rootCategories, setRootCategories] = useState([]);
  const [loadingNodes, setLoadingNodes] = useState({});
  const containerRef = useRef(null);
  const hasLoadedRootRef = useRef(false);
  const selectedRef = useRef(null); // for auto-scroll 
  // Close on outside click
  useEffect(() => {
    function handleClickOutside(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
	// Listen at the very top level (window)
    window.addEventListener("click", handleClickOutside, true);
    window.addEventListener("focusin", handleClickOutside); // catches focus events (e.g. inputs)

    return () => {
      window.removeEventListener("click", handleClickOutside, true);
      window.removeEventListener("focusin", handleClickOutside);
    };
	  
  }, []);

  // Initial root fetch only once
  useEffect(() => {
    if (hasLoadedRootRef.current) return;
    hasLoadedRootRef.current = true;

    fetchData(null).then((data) => {
      const rootData = [
        { id: "all", name: "All categories", isParent: false },
        ...data.map((c) => ({ ...c, pId: "all" })),
      ];
      setCategories(rootData);
      setRootCategories(rootData);
    });
  }, []);

useEffect(() => {
  setSelectedId(value || null);
}, [value]);

useEffect(() => {
  setSelectedPath(fullPath || "");
}, [fullPath]);
 
	
  const fetchData = async (parentId) => {
    const res = await fetch(`${apiUrl}?parent=${parentId || ""}`, {
      method: "GET",
      credentials: "include",
    });

    const resJson = await res.json();

    if (Array.isArray(resJson)) {
      return resJson;
    } else if (resJson && Array.isArray(resJson.children)) {
      return resJson.children;
    } else {
      return [];
    }
  };

  const searchData = async (term) => {
    setLoading(true);
    setIsSearchMode(true);
    try {
      const res = await fetch(
        `${apiUrl}?search=${encodeURIComponent(term)}`,
        { method: "GET", credentials: "include" }
      );
      const flatData = await res.json();

      if (!flatData || flatData.length === 0) {
        setCategories([{ id: "all", name: "All categories", isParent: false }]);
        setAutoExpanded({});
        return;
      }

      const treeData = buildTreeWithAncestors(flatData);
      const sortedTreeData = sortTree(treeData);

      setCategories([
        { id: "all", name: "All categories", isParent: false },
        ...sortedTreeData,
      ]);
      const expandedFromSearch = expandAncestors(flatData);
      setAutoExpanded(expandedFromSearch);
		
		
    /*  const expandedFromSearch = {};
      flatData.forEach((n) => {
        if (n.id) expandedFromSearch[n.id] = true;
        if (n.pId && n.pId !== "0") expandedFromSearch[n.pId] = true;
      }); 
      setAutoExpanded(expandedFromSearch);*/
    } finally {
      setLoading(false);
    }
  };
   function expandAncestors(flatData) {
	  const expanded = {};
	  const idMap = {};
	  flatData.forEach((n) => { idMap[n.id] = n; });

	  flatData.forEach((n) => {
		let current = n;
		while (current && current.pId && current.pId !== "0") {
		  expanded[current.id] = true;
		  expanded[current.pId] = true;
		  current = idMap[current.pId];
		}
	  });

	  return expanded;
   }
	
//This prevents calling searchData(val) on every single keystroke.
  const debouncedSearch = useCallback(  
    debounce((val) => {
      if (val.trim()) {
        searchData(val);
      } else {
        setIsSearchMode(false);
        setCategories(rootCategories);
		setExpanded({});
        setAutoExpanded({});
      }
    }, 300),
    [rootCategories]
  );

  useEffect(() => {
    debouncedSearch(search);
  }, [search, debouncedSearch]);

const toggleExpand = async (catId) => {
  if (isSearchMode) {
    setAutoExpanded((prev) => ({ ...prev, [catId]: !prev[catId] }));
  } else {
    setExpanded((prev) => ({ ...prev, [catId]: !prev[catId] }));
  }

  const node = findNodeById(categories, catId);
  if (node && (!node.children || node.children.length === 0)) {
    // show loading for this node
    setLoadingNodes((prev) => ({ ...prev, [catId]: true }));

    const children = await fetchData(catId);

    if (children && children.length) {
      const childrenWithPid = children.map((c) => ({ ...c, pId: catId }));
      setCategories((prev) => updateNodeChildren(prev, catId, childrenWithPid));
    } else {
      setCategories((prev) => updateNodeChildren(prev, catId, []));
    }

    // remove loading after fetch
    setLoadingNodes((prev) => {
      const updated = { ...prev };
      delete updated[catId];
      return updated;
    });
  }
};


  const findNodeById = (nodes, id) => {
    for (let n of nodes) {
      if (n.id === id) return n;
      if (n.children) {
        const found = findNodeById(n.children, id);
        if (found) return found;
      }
    }
    return null;
  };

  const updateNodeChildren = (nodes, id, children) => {
    return nodes.map((n) => {
      if (n.id === id) {
        return { ...n, children };
      }
      if (n.children) {
        return { ...n, children: updateNodeChildren(n.children, id, children) };
      }
      return n;
    });
  };

  const highlightMatch = (text, query) => {
    if (!query) return text;
    const regex = new RegExp(`(${query})`, "ig");
    return text.split(regex).map((part, i) =>
      part.toLowerCase() === query.toLowerCase() ? (
        <mark key={i} className="bg-yellow-200 text-black rounded-sm px-0.5">{part}</mark>
      ) : (
        part
      )
    );
  };

  const buildPath = (node) => {
    const path = [];
    let current = node;
    while (current) {
      if (current.name !== "All categories") {
        path.unshift(current.name);
      }
      current = findNodeById(categories, current.pId);
    }
    return path.join(" > ");
  };

  const getAncestorIds = (nodes, id, path = []) => {
    for (let node of nodes) {
      if (node.id === id) return path;
      if (node.children) {
        const found = getAncestorIds(node.children, id, [...path, node.id]);
        if (found) return found;
      }
    }
    return [];
  };

  const handleSelect = (node) => { 
	let path = "";
    if (node.id === "all") { 
      path = "All categories";
    } else {
      path = buildPath(node);
    } 
    setSelectedId(node.id);
    setSelectedPath(path);
    onSelect?.({ ...node, fullPath: path });
    onChange?.({ id: node.id, fullPath: path });
    if (!isSearchMode || (isSearchMode && !node.isParent)) {
	  setOpen(false);
	} 
  };

  useEffect(() => {
    if (open && selectedRef.current) {
      selectedRef.current.scrollIntoView({ block: "nearest" });
    }
  }, [open]);

  const renderTree = (nodes, depth = 0) => {
    return nodes.map((n) => {
      const isExpanded = expanded[n.id] || autoExpanded[n.id];
	  //const isExpanded = expanded[n.id];
      const shouldShowChildren = isExpanded; 
      return (
        <div
          key={n.id}
          ref={selectedId === n.id ? selectedRef : null}
          style={{ paddingLeft: depth * 8 }}
        > 
          <div 
			className={clsx(
				"flex items-center py-1 px-2 mr-2 text-[13px] leading-[16px] rounded-md transition-colors",
				disabled
				  ? "cursor-default text-zinc-500 hover:bg-transparent"
				  : "cursor-pointer hover:bg-gray-100 hover:text-gray-700",
				selectedId === n.id ? "bg-indigo-600 text-white" : "text-gray-900"
			  )} 
            onClick={(e) => {
			  if (disabled) return;
              e.stopPropagation();
              handleSelect(n);
            }}
          >
			  
           {(n.isParent || (n.children && n.children.length > 0)) && (
  <button
    type="button"
    onClick={(e) => {
      if (disabled) return;
      e.stopPropagation();
      toggleExpand(n.id);
    }}
    className="flex items-center mr-2 text-[10px] transition-colors"
    disabled={disabled}
  >
  
{/* Symbol box */}
  <span className="flex items-center justify-center w-3 h-3 rounded border border-black">
    <span className="flex items-center justify-center h-full text-black font-extrabold select-none">
      {isExpanded ? '−' : '+'}
    </span>
  </span>

  {/* Spinner */}
  {loadingNodes[n.id] && (
    <span className="flex items-center justify-center">
      <Spinner />
    </span>
  )} 			  
					  
  </button>
)}
			  
            <span className={clsx(
    disabled ? "text-zinc-500" : "truncate",
    depth === 0 && !(n.isParent || (n.children && n.children.length > 0) || n.id === "all") ? "ml-6" : ""
  )}>{highlightMatch(n.name, search)}</span>
          </div>

          <div
            className={`overflow-hidden transition-all duration-300 ease-in-out ${
              shouldShowChildren ? "max-h-80" : "max-h-0"
            }`}
          >
            {n.children && renderTree(n.children, depth + 1)}
          </div>
        </div>
      );
    });
  };

  return (
    <div ref={containerRef} className="relative mt-1">
      <div
className={clsx(
  "col-start-1 row-start-1 w-full appearance-none rounded-md bg-white text-left min-h-11 sm:min-h-9 pr-[calc(--spacing(7)-1px)] pl-[calc(--spacing(3.5)-1px)] sm:pl-[calc(--spacing(3)-1px)] py-[calc(--spacing(2.5)-1px)] sm:py-[calc(--spacing(1.5)-1px)] text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6 flex items-center justify-between",
  disabled ? "data-disabled:opacity-50 data-disabled:before:bg-zinc-950/5 data-disabled:before:shadow-none cursor-not-allowed" : "cursor-pointer text-gray-400"
)}
        onClick={(e) => {
		  if (disabled) return;
          e.stopPropagation();
          setOpen((o) => {
            const newOpen = !o;
            if (newOpen) {
              setSearch("");
              //setAutoExpanded({}); 
              let expandMap = { ...expanded };
              if (selectedId) {
                const ancestors = getAncestorIds(categories, selectedId);
                ancestors.forEach((id) => {
                  expandMap[id] = true;
                });
              }
			  setAutoExpanded(expandMap);
              setExpanded(expandMap);  
               if (!rootCategories.length) {
				  setLoading(true); // Trigger loading 
				  fetchData(null).then((data) => {
					const rootData = [
					  { id: "all", name: "All categories", isParent: false },
					  ...data.map((c) => ({ ...c, pId: "all" })),
					];
					setCategories(rootData);
					setRootCategories(rootData);
					setLoading(false); // Done loading 
				  });
				}
            }
            return newOpen;
          });
        }}
      >
        <span
           className={clsx(
                "truncate",
                disabled ? "text-zinc-500" : "text-gray-900"
              )}
          title={selectedPath || (selectedId ? findNodeById(categories, selectedId)?.name : "")}
        >
          {selectedPath || (selectedId ? findNodeById(categories, selectedId)?.name : "All Category")}
        </span>

        <ChevronUpDownIcon
          className={`absolute right-2 size-5 sm:size-4 text-zinc-500 transition-transform ${
            open ? "rotate-180" : ""
          }`} 
        />
      </div>

      {open && (
		  <div
			className={clsx(
			  "absolute z-10 mt-1 w-full rounded-md bg-white text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm"
			)} 
		  >
			{/* Search Box - fixed/sticky */}
			<div className="sticky top-0 bg-white z-10 p-2">
			  <Input
				placeholder="Search Category"
				value={search}
				onChange={(e) => setSearch(e.target.value)}
				disabled={disabled}
			  />
			</div>  
		    <div className="overflow-auto max-h-[15rem]">
			  {loading || (!categories.length && !isSearchMode) ? (
				<div className="p-4 text-gray-500 text-sm">Searching...</div>
			  ) : categories.length <= 1 ? (
				<div className="p-4 text-gray-500 text-sm">No records found</div>
			  ) : ( 
			  <div className="inline-block min-w-full text-[13px] leading-[16px]">
				{renderTree(categories)}
			  </div> 
			  )} 
		    </div>
		  </div>
		)}
    </div>
  );
}

// Build tree preserving pId and isParent (and any other props)
function buildTreeWithAncestors(flatData) {
  const idToNode = {};
  const idSet = new Set(flatData.map((n) => n.id));

  flatData.forEach((item) => {
    idToNode[item.id] = { ...item, children: [], pId: item.pId };
  });

  const roots = [];

  flatData.forEach((item) => {
    if (item.pId && item.pId !== "0" && idSet.has(item.pId)) {
      idToNode[item.id].pId = item.pId;
      idToNode[item.pId].children.push(idToNode[item.id]);
    } else {
      roots.push(idToNode[item.id]);
    }
  });

  return roots;
}

// Recursively sort nodes by name
function sortTree(nodes) {
  if (!nodes || nodes.length === 0) return nodes;
  nodes.sort((a, b) => a.name.localeCompare(b.name));
  nodes.forEach((node) => {
    node.children = sortTree(node.children);
  });
  return nodes;
}

function debounce(fn, delay) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
}
